import "../../style/pages/Home.scss";

import { Capacitor } from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";
import { isPlatform } from "@ionic/core";
import { IonButtons, IonContent, IonHeader, IonIcon, IonMenuButton, IonPage, IonRippleEffect, IonSelect, IonSelectOption, IonTitle, IonToolbar } from "@ionic/react";
import { bookmark } from "ionicons/icons";
import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router";

import { setActiveFrameDataPlayer, setActiveGame, setDataDisplaySettings, setModalVisibility, setPlayer, setPlayerAttr, setThemeBrightness } from "../actions";
import BrightnessToggle from "../components/BrightnessToggle";
import CharacterPortrait from "../components/CharacterPortrait";
import FrameDataSearchBar from "../components/SearchBar";
import ThemeSwitcher from "../components/ThemeSwitcher";
import App2XKOFrameData from "../constants/framedata/2XKOFrameData.json";
import AppSF3FrameData from "../constants/framedata/3SFrameData.json";
import AppGGSTFrameData from "../constants/framedata/GGSTFrameData.json";
import AppSF6FrameData from "../constants/framedata/SF6FrameData.json";
import AppSFVFrameData from "../constants/framedata/SFVFrameData.json";
import AppUSF4FrameData from "../constants/framedata/USF4FrameData.json";
import { GAME_NAMES } from "../constants/ImmutableGameDetails";
import {APP_CURRENT_VERSION_CODE, APP_CURRENT_VERSION_NAME, APP_DATE_UPDATED, VERSION_LOGS} from "../constants/VersionLogs";
import { bookmarksSelector, activeGameSelector, selectedCharactersSelector, dataDisplaySettingsSelector, appDisplaySettingsSelector } from "../selectors";
import { renameData } from "../utils";

const Home = () => {
  const FRAMEDATA_MAP = {
    "3S": AppSF3FrameData,
    USF4: AppUSF4FrameData,
    SFV: AppSFVFrameData,
    SF6: AppSF6FrameData,
    GGST: AppGGSTFrameData,
    "2XKO": App2XKOFrameData,
  };
  const bookmarks = useSelector(bookmarksSelector);
  const activeGame = useSelector(activeGameSelector);
  const selectedCharacters = useSelector(selectedCharactersSelector);
  const themeBrightness = useSelector(appDisplaySettingsSelector).themeBrightness;
  
  const dataDisplaySettings = useSelector(dataDisplaySettingsSelector);
  
  const dispatch = useDispatch();
  const history = useHistory();

  const [versionLogOrder, setVersionLogOrder]= useState(5);

  useEffect(() => {
    const checkForNewAppVersion = async () => {
      const lsCurrentVersionCode = await (await Preferences.get({key: "lsCurrentVersionCode"})).value;
  
      if (!lsCurrentVersionCode || parseInt(lsCurrentVersionCode as unknown as string) < APP_CURRENT_VERSION_CODE) {
        await Preferences.set({
          key: "lsCurrentVersionCode",
          value: APP_CURRENT_VERSION_CODE.toString(),
        });
        setVersionLogOrder(2);
      }
    };
    checkForNewAppVersion();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonMenuButton />
          </IonButtons>
          <IonTitle>FAT</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent id="Home">
        <div className="content">

          <FrameDataSearchBar
            frameDataMap={FRAMEDATA_MAP}
            onNavigate={(kind, href, gameName, characterName, characterState, moveName) => {
              dispatch(setActiveFrameDataPlayer("playerOne"));

              if (kind === "character") {
                if (gameName !== activeGame) {
                  dispatch(setActiveGame(gameName, true, characterName));
                } else if (selectedCharacters["playerOne"].name !== characterName) {
                  dispatch(setPlayer("playerOne", characterName));
                }
              } else {
                const renamedMoveObject = renameData({[moveName]: FRAMEDATA_MAP[gameName][characterName].moves[characterState][moveName]}, dataDisplaySettings.moveNameType, dataDisplaySettings.inputNotationType);
                const userChosenName = Object.keys(renamedMoveObject)[0];
                console.log(gameName);
                if (gameName !== activeGame) {
                  dispatch(setActiveGame(gameName, true, characterName, characterState, userChosenName));
                } else {
                  dispatch(setPlayerAttr("playerOne", characterName, { selectedMove: userChosenName, vtState: characterState }));
                }
              }
                  
              history.push(href);
            }}
          />

          <div className="two-column-grid">
            <div className="column main-column">

              {!Capacitor.isNativePlatform() &&
              <>
                {/* Welcome panel wide */}
                <div className="panel welcome-panel-wide">
                  <img src="https://fullmeter.com/images/global/fat-icon.png" />
                  <div>
                    <h1>Welcome to FAT!</h1>
                    <p>We've got native apps!</p>
                    <div className="app-badge-holder">
                      <a href="https://itunes.apple.com/us/app/frame-assistant-tool/id886775464" target="_blank">
                        <img src="https://fullmeter.com/images/global/ios-badge.svg" />
                      </a>
                      <a href="https://play.google.com/store/apps/details?id=com.fullmeter.fat" target="_blank">
                        <img src="https://fullmeter.com/images/global/android-badge.png" />
                      </a>
                    </div>
                  </div>
                </div>

                {/* Welcome panel mobile */}
                <div className="panel welcome-panel-mobile">
                  <div className="info">
                    <img src="https://fullmeter.com/images/global/fat-icon.png" />
                    <div>
                      <h1>Welcome to FAT!</h1>
                      <p>We've got native apps!</p>
                    </div>
                  </div>
                  <div className="app-badge-holder">
                    <a href="https://itunes.apple.com/us/app/frame-assistant-tool/id886775464" target="_blank">
                      <img src="https://fullmeter.com/images/global/ios-badge.svg" />
                    </a>
                    <a href="https://play.google.com/store/apps/details?id=com.fullmeter.fat" target="_blank">
                      <img src="https://fullmeter.com/images/global/android-badge.png" />
                    </a>
                  </div>
                </div>
              </>}

              {/* Game select panel */}
              <div className="panel game-select-panel">
                <h2>Select a game</h2>
                <div className="game-buttons">
                  {GAME_NAMES.map(gameName => 
                    <button className={Capacitor.isNativePlatform() ? "fake-logo" : ""} key={gameName} onClick={() => {
                      dispatch(setActiveFrameDataPlayer("playerOne"));
                      if (gameName !== activeGame) {
                        dispatch(setActiveGame(gameName, true));
                      }
                      dispatch(setModalVisibility({ currentModal: "characterSelect", visible: true }));
                    }}>
                      <img src={!Capacitor.isNativePlatform() ? `https://fullmeter.com/fatassets/img/logos/${gameName}-logo.png` : `${process.env.PUBLIC_URL}/assets/images/logos/${gameName}-logo.svg`} />
                    </button>
                  )}
                </div>
              </div>

              {/* Version log panel */}
              <div className="panel version-panel" style={{order: versionLogOrder, backgroundColor: versionLogOrder === 2 ? "var(--fat-whatsnew-bg)" : ""}}>
                <h2 style={{color: versionLogOrder === 2 ? "#ffffff" : ""}}>{ versionLogOrder === 2 && "What's New - "}Version {APP_CURRENT_VERSION_NAME}</h2>
                <p>Updated {APP_DATE_UPDATED}</p>

                {Object.keys(VERSION_LOGS[APP_CURRENT_VERSION_NAME]).map(heading =>
                  (!isPlatform("ios") && heading.toLowerCase().includes("ios")) || (!isPlatform("android") && heading.toLowerCase().includes("android")) ?
                    false
                    : (
                      <div key={heading}>
                        <h3 className={heading.includes("Bug Fixes") || heading.includes("Hotfix") ? "bug" : "feature"}>{heading}</h3>
                        <ul>
                          {VERSION_LOGS[APP_CURRENT_VERSION_NAME][heading].map((newThing, index) =>
                            !(!isPlatform("ios") && newThing.toLowerCase().startsWith("ios") || !isPlatform("android") && newThing.toLowerCase().startsWith("android")) &&
                              <li key={heading+index}>
                                <ReactMarkdown
                                  components={{
                                    p: ({ children }) => <span>{children}</span>,
                                  }}
                                  children={newThing} />
                              </li>
                                      
                          )}
                        </ul>
                      </div>
                    )
                )}
                
              </div>

            </div>

            <div className="column side-column">
              {/* Quick Settings Panel */}
              <div className="panel settings-panel">
                <h2>Quick Settings</h2>
                <div className="setting-entry ion-activatable">
                  <IonSelect
                    label={"Move Names"}
                    interface="popover"
                    value={dataDisplaySettings.moveNameType}
                    okText="Select"
                    cancelText="Cancel"
                    onIonChange={(e) => {
                      dispatch(setDataDisplaySettings({moveNameType: e.detail.value}));
                      dispatch(setPlayer("playerOne", selectedCharacters.playerOne.name));
                      dispatch(setPlayer("playerTwo", selectedCharacters.playerTwo.name));
                    }}
                  >
                    <IonSelectOption value="official">Official</IonSelectOption>
                    <IonSelectOption value="common">Common</IonSelectOption>
                    <IonSelectOption value="inputs">Inputs</IonSelectOption>
                  </IonSelect>
                  <IonRippleEffect/>

                </div>

                <div className="setting-entry ion-activatable">
                  <IonSelect
                    label={"Input Notation"}
                    interface="popover"
                    value={dataDisplaySettings.inputNotationType}
                    okText="Select"
                    cancelText="Cancel"
                    onIonChange={e => {
                      dispatch(setDataDisplaySettings({inputNotationType: e.detail.value}));
                      if (dataDisplaySettings.moveNameType === "inputs" || dataDisplaySettings.inputNotationType === "ezCmd" || e.detail.value === "ezCmd") {
                        dispatch(setPlayer("playerOne", selectedCharacters.playerOne.name));
                        dispatch(setPlayer("playerTwo", selectedCharacters.playerTwo.name));
                      }
                    }}
                  >
                    <IonSelectOption value="plnCmd">Motion</IonSelectOption>
                    <IonSelectOption value="numCmd">NumPad</IonSelectOption>
                    {activeGame === "SF6" && <IonSelectOption value="ezCmd">Modern</IonSelectOption>}
                  </IonSelect>
                  <IonRippleEffect/>
                </div>
                <div
                  className="setting-entry dark-mode-entry ion-activatable"
                  onClick={() => dispatch(setThemeBrightness(themeBrightness === "light" ? "dark" : "light"))}
                >
                  <label>Dark Mode</label>
                  <BrightnessToggle displayOnly></BrightnessToggle>
                  <IonRippleEffect />
                </div>
                {Capacitor.isNativePlatform() &&
                  <div className="setting-entry theme-entry">
                    <label>Theme</label>
                    <ThemeSwitcher premiumPreview={false} lines={false} />
                  </div>
                }

              </div>
              
              <div className="panel bookmark-panel">
                <h2>Bookmarks</h2>
                <div className="bookmark-list">
                  {bookmarks.map((bookmark, index) => {
                  // MOVE-DETAIL BOOKMARK
                    if (bookmark.modeName === "movedetail") {
                      const renamedMoveObject = bookmark.modeName ==="movedetail" ? renameData({[bookmark.moveName]: FRAMEDATA_MAP[bookmark.gameName][bookmark.characterName].moves[bookmark.vtState][bookmark.moveName]}, dataDisplaySettings.moveNameType, dataDisplaySettings.inputNotationType) : [""];
                      const userChosenName = Object.keys(renamedMoveObject)[0];
                      return (
                        <div className="bookmark-entry"
                          key={`stat-${bookmark.gameName}-${bookmark.characterName}-${bookmark.moveName}`}
                          onClick={() => {
                            dispatch(setActiveFrameDataPlayer("playerOne"));
                            if (bookmark.gameName !== activeGame) {
                              dispatch(setActiveGame(bookmark.gameName, true, bookmark.characterName, bookmark.vtState, userChosenName));
                            } else {
                              dispatch(setPlayerAttr("playerOne", bookmark.characterName, { selectedMove: userChosenName, vtState: bookmark.vtState }));
                            }
                            history.push(`/${bookmark.modeName}/${bookmark.gameName}/${bookmark.characterName}/${bookmark.vtState}/${bookmark.moveName}`);
                          }}
                        >
                          <CharacterPortrait
                            charName={bookmark.characterName}
                            game={bookmark.gameName}
                            charColor={"#ffff00"}
                            remoteImage={false}
                            showName={false}
                            selected={true}
                          ></CharacterPortrait>
                          <div className="bio">
                            <p>{bookmark.gameName}</p>
                            <h1>{bookmark.moveName ? userChosenName : bookmark.characterName}</h1>
                          </div>
                        </div>

                      );
                    } else {
                      return (
                      // CHARACTER BOOKMARK
                        <div className="bookmark-entry"
                          key={`stat-${bookmark.gameName}-${bookmark.characterName}-${index}`}
                          onClick={() => {
                            dispatch(setActiveFrameDataPlayer("playerOne"));
                
                            if (bookmark.gameName !== activeGame) {
                              dispatch(setActiveGame(bookmark.gameName, true, bookmark.characterName));
                            } else if (selectedCharacters["playerOne"].name !== bookmark.characterName) {
                              dispatch(setPlayer("playerOne", bookmark.characterName));
                            }
                
                            const bookmarkURL = `/${bookmark.modeName}/${bookmark.gameName}/${bookmark.characterName}`;
                            history.push(bookmarkURL);
                          }}
                        >
                          <CharacterPortrait
                            charName={bookmark.characterName}
                            game={bookmark.gameName}
                            charColor={"#ffff00"}
                            remoteImage={false}
                            showName={false}
                            selected={true}
                          ></CharacterPortrait>
                          <div className="bio">
                            <p>{bookmark.gameName}</p>
                            <h1>{bookmark.moveName ? "userChosenName" : bookmark.characterName}</h1>
                          </div>
                        </div>

                      );
                    }
                  })}
                </div>
                
                {bookmarks.length === 0 &&
                  <div className="bookmark-message-container">
                    <p>{Capacitor.isNativePlatform() ? "Tap" : "Click"} a bookmark <IonIcon icon={bookmark}/> anywhere in the app to see it here</p>
                  </div>
                }
              </div>
            </div>
          </div>
          
        </div>      
      </IonContent>
    </IonPage>
  );
};

export default Home;